import getCloudfrontCdn from '@anm/helpers/getCloudfrontCdn';
import { getType } from '@anm/helpers/saga/typesafe-actions';
import { Password } from '@anm/hosting-player';
import { Reducer } from 'redux';

import { getEmbedDataDefaultState, getUpdatedConfigs } from '../helpers';
import { HostedVideoActions } from '../types';

import {
  clearVideoData,
  discardConfigsChanges,
  discardPasswordChanges,
  discardPlayerColorChanges,
  discardPosterChanges,
  setVideoData,
  updateConfigs,
  updatePlayerColor,
  updatePoster,
  updateVideoPassword
} from './../actions';
import { initialHostedVideoState } from './index';

export const defaultPasswordState: Password = {
  password: '',
  description: 'Please enter password to watch this video'
};

const videoDataReducer: Reducer<typeof initialHostedVideoState, HostedVideoActions> = (
  state = initialHostedVideoState,
  action
) => {
  switch (action.type) {
    case getType(setVideoData): {
      const { hostId, videoData, embedStatus } = action.payload;

      const mappedPoster = getCloudfrontCdn({ url: videoData.poster.posterUrl || '', isLogged: true });
      const mappedBestQualityVideo = getCloudfrontCdn({ url: videoData.bestQualityVideo || '', isLogged: true });
      const mappedSources = videoData.sources.map(s => ({
        ...s,
        path: getCloudfrontCdn({ url: s.path, isLogged: true }) || ''
      }));

      const passwordStatus =
        typeof videoData.password.isEnabled === 'undefined'
          ? !!videoData.password.password?.length
          : videoData.password.isEnabled;

      const passwordState: Password = {
        password: videoData.password.password || defaultPasswordState.password,
        description: videoData.password.description || defaultPasswordState.description,
        isEnabled: passwordStatus
      };

      const commonVideoData = {
        ...videoData,
        sources: mappedSources,
        poster: {
          ...state.videoData?.poster,
          posterUrl: mappedPoster
        },
        bestQualityVideo: mappedBestQualityVideo,
        password: passwordState
      };

      return {
        ...state,
        id: hostId,
        embedVideo: {
          ...state.embedVideo,
          status: embedStatus,
          data: getEmbedDataDefaultState(hostId, videoData)
        },
        videoData: {
          ...commonVideoData,
          savedData: {
            ...commonVideoData
          }
        }
      };
    }

    case getType(updateConfigs): {
      const newConfigs = action.payload;
      const playerColor = state.videoData?.customConfigs.playerColor;

      const { newConfigsWithColor, updatedPlayerConfigs } = getUpdatedConfigs({
        playerColor,
        configs: newConfigs,
        sources: state.videoData?.sources
      });

      return {
        ...state,
        videoData: {
          ...state.videoData!,
          configs: {
            ...state.videoData?.configs,
            ...newConfigsWithColor
          },
          customConfigs: {
            ...state.videoData?.customConfigs,
            ...updatedPlayerConfigs
          }
        }
      };
    }

    case getType(discardConfigsChanges): {
      const playerColor = state.videoData?.customConfigs.playerColor;

      const { newConfigsWithColor, updatedPlayerConfigs } = getUpdatedConfigs({
        playerColor,
        configs: state.videoData?.savedData.configs!,
        sources: state.videoData?.sources
      });

      return {
        ...state,
        videoData: {
          ...state.videoData!,
          configs: {
            ...state.videoData?.configs,
            ...newConfigsWithColor
          },
          customConfigs: {
            ...state.videoData?.customConfigs,
            ...updatedPlayerConfigs
          }
        }
      };
    }

    case getType(updatePlayerColor): {
      const newColor = action.payload;

      return {
        ...state,
        videoData: {
          ...state.videoData!,
          configs: {
            ...state.videoData?.configs!,
            playerColor: newColor
          },
          customConfigs: {
            ...state.videoData?.customConfigs,
            playerColor: newColor
          }
        },
        embedVideo: {
          ...state.embedVideo,
          data: {
            ...state.embedVideo.data!,
            popover: {
              ...state.embedVideo.data!.popover,
              button: {
                ...state.embedVideo.data!.popover.button,
                color: newColor
              }
            }
          }
        }
      };
    }

    case getType(discardPlayerColorChanges): {
      const prevColor = state.videoData?.savedData.configs.playerColor;

      return {
        ...state,
        videoData: {
          ...state.videoData!,
          configs: {
            ...state.videoData?.configs!,
            ...(prevColor && { playerColor: prevColor })
          },
          customConfigs: {
            ...state.videoData?.customConfigs,
            playerColor: prevColor
          }
        }
      };
    }

    case getType(updatePoster): {
      const newPoster = action.payload;

      return {
        ...state,
        videoData: {
          ...state.videoData!,
          poster: newPoster
        }
      };
    }

    case getType(discardPosterChanges): {
      return {
        ...state,
        videoData: {
          ...state.videoData!,
          poster: state.videoData?.savedData.poster!
        }
      };
    }

    case getType(updateVideoPassword): {
      const { security } = action.payload;

      return {
        ...state,
        videoData: {
          ...state.videoData!,
          password: {
            ...state.videoData?.password,
            ...security
          }
        }
      };
    }

    case getType(discardPasswordChanges): {
      return {
        ...state,
        videoData: {
          ...state.videoData!,
          password: state.videoData?.savedData.password!
        }
      };
    }

    case getType(clearVideoData): {
      return {
        ...state,
        videoData: initialHostedVideoState.videoData
      };
    }

    default:
      return state;
  }
};

export default videoDataReducer;
